/**
 * useFeatureLastVisited — reads "last visited" timestamps written by
 * `useRouteTracker` and returns a relative-day count plus a status pill kind.
 */
import { useSyncExternalStore } from 'react';
import { readLastVisit } from './useRouteTracker';

export type FeatureStatus =
  | { kind: 'fresh'; daysAgo: 0 }
  | { kind: 'recent'; daysAgo: number }
  | { kind: 'stale'; daysAgo: number }
  | { kind: 'never' };

function classify(daysAgo: number | null): FeatureStatus {
  if (daysAgo === null) return { kind: 'never' };
  if (daysAgo === 0) return { kind: 'fresh', daysAgo: 0 };
  if (daysAgo <= 7) return { kind: 'recent', daysAgo };
  return { kind: 'stale', daysAgo };
}

function subscribe(callback: () => void) {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
}

export function useFeatureLastVisited(route: string): FeatureStatus {
  // Re-read on storage events so cross-tab updates flow through.
  const ts = useSyncExternalStore(
    subscribe,
    () => readLastVisit(route),
    () => null,
  );
  if (!ts) return { kind: 'never' };
  const daysAgo = Math.floor((Date.now() - ts) / 86400000);
  return classify(daysAgo);
}

export function statusLabel(status: FeatureStatus): string {
  switch (status.kind) {
    case 'never':
      return 'Not started';
    case 'fresh':
      return 'Visited today';
    case 'recent':
      return status.daysAgo === 1 ? 'Visited yesterday' : `Visited ${status.daysAgo}d ago`;
    case 'stale':
      return `Last used ${status.daysAgo}d ago`;
  }
}